import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, Plus, Pencil, Trash2, Loader2, AlertCircle,
  Globe, Ban, CheckCircle2, Clock, ChevronRight, X, Upload, Building2, Award,
} from "lucide-react";
import {
  useAdminGetEventsQuery,
  useAdminCreateEventMutation,
  useAdminUpdateEventMutation,
  useAdminDeleteEventMutation,
  useAdminPublishEventMutation,
  useAdminCancelEventMutation,
  useAdminGetCertificateTemplatesQuery,
} from "../api/adminEventsApi";
import { useAdminGetAccreditedInstitutesQuery } from "../api/accreditedInstitutesApi";
import { useGetChaptersQuery } from "../../chapter/api/chapterApi";
import { useGetActiveUserCategoriesQuery } from "../../settings/api/settingsApi";

import { CertificatePreview, sampleCertificate } from "../components/Certificate";
import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? "An error occurred";
}

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

const STATUS_COLORS: Record<string, string> = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-100",
  draft:     "bg-amber-50 text-amber-700 border-amber-100",
  cancelled: "bg-rose-50 text-rose-700 border-rose-100",
  completed: "bg-gray-50 text-gray-500 border-gray-100",
};

type HostedBy = "niqs" | "chapter" | "institute";

/**
 * One signature block on the certificate: the office, who holds it, and their
 * scanned signature.
 *
 * `signature_path` is what the server stored and is sent back untouched to
 * mean "keep this block's scan"; `signature_url` is only for showing it here;
 * `file` is a newly chosen scan that has not been uploaded yet. Clearing a
 * signature means emptying all three.
 */
type CertSignature = {
  name: string;
  title: string;
  signature_path: string;
  signature_url: string;
  file: File | null;
};

const emptySignature: CertSignature = {
  name: "", title: "", signature_path: "", signature_url: "", file: null,
};

/** What a certificate's foot can hold before the blocks run out of width. */
const MAX_SIGNATURES = 4;

type EventForm = {
  title: string; theme: string; tagline: string; details: string;
  location: string; start_date: string; end_date: string; year: string;
  is_paid: boolean; price: string;
  early_bird_ends_at: string; late_starts_at: string;
  is_cpd: boolean; cpd_points: string;
  /** Key of the certificate design, or "" for an event that issues none. */
  certificate_template: string;
  /** Who signs this event's certificate, in the order the blocks print. */
  certificate_signatures: CertSignature[];
  is_members_only: boolean; has_refreshment: boolean; has_materials: boolean;
  capacity: string;
  hosted_by: HostedBy;
  host_chapter_id: string;
  host_accredited_institute_id: string;
};

const emptyForm: EventForm = {
  title: "", theme: "", tagline: "", details: "", location: "",
  start_date: "", end_date: "", year: "", is_paid: false, price: "",
  early_bird_ends_at: "", late_starts_at: "",
  is_cpd: false, cpd_points: "", certificate_template: "", certificate_signatures: [],
  is_members_only: false,
  has_refreshment: false, has_materials: false, capacity: "",
  hosted_by: "niqs", host_chapter_id: "", host_accredited_institute_id: "",
};

// Public (non-member) price tiers — chosen by guests at registration. Member
// grades are added dynamically from the user_categories table below.
type PriceRow = { key: string; label: string; user_category_id: number | null };
//
// Inductees are deliberately NOT here. An inductee's conference fee is
// collected on their upgrade payment, the way the last probationer induction
// was — they never pay from the events page, so offering the tier would only
// invite the same seat to be charged twice.
const PUBLIC_TIERS: PriceRow[] = [
  { key: "student_unregistered", label: "Student not Registered with NIQS",  user_category_id: null },
  { key: "lecturer",             label: "Lecturers",                         user_category_id: null },
  { key: "non_member",           label: "Non-Members",                       user_category_id: null },
  { key: "foreign_participant",  label: "Foreign Participants",              user_category_id: null },
];

/**
 * One tier's fees. `normal` is the fee the tier charges outside any window and
 * the only one that is required — a tier priced once (a foreign participant, a
 * non-member) fills in nothing else. `early` and `late` are what the SAME tier
 * costs inside the two windows, and `virtual` is that grade's fee for a stream
 * rather than a seat, which the schedule prices flat.
 *
 * `idCard` makes the tier ask for proof at registration. It belongs to the
 * category, not the event: only the rates that are open to a particular kind of
 * person — a student, a foreign delegate — have anything to check.
 */
type TierFees = { early: string; normal: string; late: string; virtual: string; idCard: boolean };
const emptyFees: TierFees = { early: "", normal: "", late: "", virtual: "", idCard: false };

/** The virtual seat is a second priced row against the same grade. */
const virtualKey = (key: string) => `${key}_virtual`;

function AdminEventsPageInner() {
  const { data: events = [], isLoading, refetch } = useAdminGetEventsQuery();
  const { data: chapters = [] }                = useGetChaptersQuery();
  const { data: institutes = [] }              = useAdminGetAccreditedInstitutesQuery();
  const { data: userCategories = [] }          = useGetActiveUserCategoriesQuery();

  // Member-grade tiers come from user_categories (excluding Firm); each carries
  // its user_category_id so registration matches a member's grade exactly.
  const priceRows: PriceRow[] = [
    ...(userCategories as any[])
      .filter((c) => String(c.name).toLowerCase() !== "firm")
      .map((c) => ({ key: `uc_${c.id}`, label: c.name, user_category_id: Number(c.id) })),
    ...PUBLIC_TIERS,
  ];
  const [createEvent, { isLoading: creating }] = useAdminCreateEventMutation();
  const [updateEvent, { isLoading: updating }] = useAdminUpdateEventMutation();
  const [deleteEvent, { isLoading: deleting }] = useAdminDeleteEventMutation();
  const [publishEvent] = useAdminPublishEventMutation();
  const [cancelEvent]  = useAdminCancelEventMutation();

  const [showForm, setShowForm]     = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [form, setForm]             = useState<EventForm>(emptyForm);
  const [bgFile, setBgFile]         = useState<File | null>(null);
  const [fgFile, setFgFile]         = useState<File | null>(null);
  // Per-tier fees (strings for input binding), keyed by the tier's base key.
  // The virtual fee lives on the same entry but is saved as its own priced row.
  const [catPrices, setCatPrices]   = useState<Record<string, TierFees>>({});
  const [formError, setFormError]   = useState("");
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleteError, setDeleteError]   = useState("");
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [filterHost, setFilterHost]         = useState<"all" | HostedBy>("all");
  const [filterChapterId, setFilterChapterId]     = useState("");
  const [filterInstituteId, setFilterInstituteId] = useState("");

  // The two window columns only appear once there is a window to price into —
  // a chapter CPD day with one flat fee should not be asked for three.
  const showEarly = !!form.early_bird_ends_at;
  const showLate  = !!form.late_starts_at;
  // Whether anything is currently priced for the stream, which decides the
  // wording of the note above the grid.
  const hasVirtualSeats = Object.values(catPrices).some((f) => (f?.virtual ?? "").trim() !== "");

  const set = (k: keyof EventForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const toggle = (k: keyof EventForm) =>
    setForm((p) => ({ ...p, [k]: !p[k] }));

  const openCreate = () => {
    setEditTarget(null); setForm(emptyForm);
    setBgFile(null); setFgFile(null);
    setCatPrices({});
    setFormError(""); setShowForm(true);
  };

  const openEdit = (ev: any) => {
    setEditTarget(ev);
    setForm({
      title: ev.title ?? "", theme: ev.theme ?? "", tagline: ev.tagline ?? "",
      details: ev.details ?? "", location: ev.location ?? "",
      start_date: ev.start_date?.slice(0, 10) ?? "",
      end_date: ev.end_date?.slice(0, 10) ?? "",
      year: ev.year != null ? String(ev.year) : "",
      is_paid: !!ev.is_paid, price: ev.price ?? "",
      early_bird_ends_at: ev.early_bird_ends_at?.slice(0, 10) ?? "",
      late_starts_at: ev.late_starts_at?.slice(0, 10) ?? "",
      is_cpd: !!ev.is_cpd, cpd_points: ev.cpd_points ?? "",
      certificate_template: ev.certificate_template ?? "",
      certificate_signatures: ((ev.certificate_signatures ?? []) as any[]).map((x) => ({
        ...emptySignature,
        name:           x?.name ?? "",
        title:          x?.title ?? "",
        signature_path: x?.signature_path ?? "",
        signature_url:  x?.signature_url ?? "",
      })),
      is_members_only: !!ev.is_members_only,
      has_refreshment: !!ev.has_refreshment,
      has_materials: !!ev.has_materials,
      capacity: ev.capacity ?? "",
      hosted_by: (ev.hosted_by as HostedBy) ?? "niqs",
      host_chapter_id: ev.host_chapter_id ?? "",
      host_accredited_institute_id: ev.host_accredited_institute_id ? String(ev.host_accredited_institute_id) : "",
    });
    setBgFile(null); setFgFile(null);
    // Two saved rows fold back into one editable line: the physical row brings
    // the three timeline fees, the virtual row brings the fourth.
    const cp: Record<string, TierFees> = {};
    const num = (v: any) => (v == null || v === "" ? "" : String(Number(v)));
    (ev.category_prices ?? []).forEach((p: any) => {
      if (p.attendance_mode === "virtual") {
        const base = String(p.category_key).replace(/_virtual$/, "");
        cp[base] = { ...emptyFees, ...(cp[base] ?? {}), virtual: num(p.amount) };
        return;
      }
      cp[p.category_key] = {
        ...emptyFees,
        ...(cp[p.category_key] ?? {}),
        normal: num(p.amount),
        early:  num(p.early_bird_amount),
        late:   num(p.late_amount),
        idCard: !!p.requires_id_card,
      };
    });
    setCatPrices(cp);
    setFormError(""); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditTarget(null); };

  const handleSubmit = async () => {
    setFormError("");
    setMsg(null); // clear any stale success banner before we try
    if (!form.title.trim()) { setFormError("Title is required"); return; }
    if (!form.start_date)   { setFormError("Start date is required"); return; }
    if (!form.end_date)     { setFormError("End date is required"); return; }
    if (form.early_bird_ends_at && form.late_starts_at
        && form.late_starts_at <= form.early_bird_ends_at) {
      setFormError("The late fee must start after the early bird period ends.");
      return;
    }

    // Early bird and late are fees ON a category; without a Normal fee there is
    // no category for them to be on, and the row would be dropped on save. Said
    // out loud rather than swallowed — a fee that vanishes silently is how a
    // delegate ends up charged the wrong one.
    if (form.is_paid) {
      const orphan = priceRows.find((t) => {
        const f = catPrices[t.key] ?? emptyFees;
        return (f.normal ?? "").trim() === ""
          && (((f.early ?? "").trim() !== "") || ((f.late ?? "").trim() !== ""));
      });
      if (orphan) {
        setFormError(
          `${orphan.label}: enter a Normal fee. Early bird and late are optional, but a category needs its normal fee to be offered at all.`,
        );
        return;
      }
    }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      // Skip empty host fields that don't apply
      if (k === "host_chapter_id" && form.hosted_by !== "chapter") return;
      if (k === "host_accredited_institute_id" && form.hosted_by !== "institute") return;
      // Sent below as JSON — String() on an array of objects yields
      // "[object Object]", which the API would reject as unreadable.
      if (k === "certificate_signatures") return;
      fd.append(k, String(v === true ? 1 : v === false ? 0 : v));
    });

    // Always sent, so clearing the last signature on an edit actually clears
    // it. Multipart cannot express an empty list any other way, and a field
    // that simply vanishes reads on the server as "leave it alone".
    //
    // The scans are keyed by POSITION IN THIS LIST, so both are built from the
    // same filtered array — filtering twice would drift the moment a block
    // without an office sat between two that had one.
    const sigRows = form.certificate_template
      ? form.certificate_signatures.filter((x) => x.title.trim() !== "")
      : [];

    fd.append(
      "certificate_signatures",
      JSON.stringify(
        sigRows.map((x) => ({
          name: x.name.trim() || null,
          title: x.title.trim(),
          signature_path: x.signature_path || null,
        })),
      ),
    );

    sigRows.forEach((x, i) => {
      if (x.file) fd.append(`certificate_signature_images[${i}]`, x.file);
    });
    if (bgFile) fd.append("background_image", bgFile);
    if (fgFile) fd.append("foreground_image", fgFile);

    // Per-category prices (only tiers with an amount). Always sent when paid so
    // clearing all of them removes tiered pricing on the server.
    //
    // One line in the form becomes up to two priced rows: the seat in the hall,
    // carrying its early-bird and late fees, and the stream, which the approved
    // schedule prices flat.
    if (form.is_paid) {
      let i = 0;
      const push = (row: Record<string, string>) => {
        Object.entries(row).forEach(([k, v]) => fd.append(`category_prices[${i}][${k}]`, v));
        i++;
      };

      priceRows.forEach((t) => {
        const fees = catPrices[t.key] ?? emptyFees;
        const normal  = (fees.normal  ?? "").trim();
        const early   = (fees.early   ?? "").trim();
        const late    = (fees.late    ?? "").trim();
        const virtual = (fees.virtual ?? "").trim();

        // The Normal fee is what makes a category offered at all. Early bird
        // and late are optional extras ON it, so a line with no Normal fee is
        // not a category — handleSubmit refuses it before we get here rather
        // than dropping it silently.
        if (normal !== "") {
          push({
            category_key: t.key,
            label: t.label,
            attendance_mode: "physical",
            requires_id_card: fees.idCard ? "1" : "0",
            amount: normal,
            // Sent empty rather than omitted so clearing an early-bird fee
            // actually removes it instead of leaving the old one in place.
            early_bird_amount: early,
            late_amount: late,
            ...(t.user_category_id != null ? { user_category_id: String(t.user_category_id) } : {}),
          });
        }

        if (virtual !== "") {
          push({
            category_key: virtualKey(t.key),
            label: `${t.label} (Virtual)`,
            attendance_mode: "virtual",
            // The same category, so the same proof — a student rate online is
            // still a student rate.
            requires_id_card: fees.idCard ? "1" : "0",
            amount: virtual,
            ...(t.user_category_id != null ? { user_category_id: String(t.user_category_id) } : {}),
          });
        }
      });
    }

    try {
      if (editTarget) {
        await updateEvent({ id: editTarget.id, body: fd }).unwrap();
        setMsg({ type: "success", text: "Event updated" });
      } else {
        await createEvent(fd).unwrap();
        setMsg({ type: "success", text: "Event created" });
        // Reset filter so the new event is visible, then force a refetch
        setFilterHost("all");
        setFilterChapterId("");
        setFilterInstituteId("");
        refetch();
      }
      closeForm();
    } catch (err) { setFormError(extractError(err)); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await deleteEvent(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      setMsg({ type: "success", text: "Event deleted" });
    } catch (err) { setDeleteError(extractError(err)); }
  };

  const handlePublish = async (ev: any) => {
    try { await publishEvent(ev.id).unwrap(); setMsg({ type: "success", text: "Event published" }); }
    catch (err) { setMsg({ type: "error", text: extractError(err) }); }
  };

  const handleCancel = async (ev: any) => {
    try { await cancelEvent(ev.id).unwrap(); setMsg({ type: "success", text: "Event cancelled" }); }
    catch (err) { setMsg({ type: "error", text: extractError(err) }); }
  };

  const textareaCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm resize-none";

  const published = (events as any[]).filter((e: any) => e.status === "published").length;
  const drafts    = (events as any[]).filter((e: any) => e.status === "draft").length;

  const filtered = (events as any[]).filter((e: any) => {
    if (filterHost !== "all" && e.hosted_by !== filterHost) return false;
    if (filterChapterId && e.host_chapter_id !== filterChapterId) return false;
    if (filterInstituteId && String(e.host_accredited_institute_id) !== filterInstituteId) return false;
    return true;
  });

  return (
    <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: "Total",     val: (events as any[]).length, icon: CalendarCheck, color: "indigo"  },
          { label: "Published", val: published,                icon: Globe,         color: "emerald" },
          { label: "Drafts",    val: drafts,                   icon: Clock,         color: "amber"   },
          { label: "Cancelled", val: (events as any[]).filter((e: any) => e.status === "cancelled").length, icon: Ban, color: "rose" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-4 sm:p-5 rounded-2xl sm:rounded-3xl shadow-sm flex items-center gap-3 sm:gap-4">
            <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5 truncate">{s.label}</div>
              <div className="text-lg sm:text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
          <div className="flex items-center gap-4 sm:gap-5">
            <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200 shrink-0">
              <CalendarCheck className="w-5 h-5 sm:w-7 sm:h-7" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-black text-gray-900 tracking-tight leading-tight uppercase">Events Management</h1>
              <p className="text-gray-400 text-xs sm:text-sm mt-1 sm:mt-1.5 font-medium">Create and manage all organisational events.</p>
            </div>
          </div>
          <AccessGate perm={PERMS.event.create}>
            <button
              onClick={openCreate}
              className="w-full md:w-auto flex items-center justify-center gap-2 bg-gray-900 text-white font-black text-sm px-6 py-3 rounded-2xl hover:bg-gray-800 transition-colors shadow-xl shadow-gray-200"
            >
              <Plus className="w-4 h-4" /> New Event
            </button>
          </AccessGate>
        </div>
      </div>

      {/* ── Filter bar ─────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-sm flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        {/* Host type tabs. Four of them do not fit a narrow phone, so the strip
            scrolls sideways rather than wrapping into two ragged rows. */}
        <div className="-mx-1 px-1 overflow-x-auto no-scrollbar sm:overflow-visible sm:shrink-0">
          <div className="flex gap-1 p-1 bg-gray-50 border rounded-2xl w-max sm:w-auto">
            {([
              { id: "all",      label: "All" },
              { id: "niqs",     label: "NIQS" },
              { id: "chapter",  label: "Chapter" },
              { id: "institute", label: "Institution" },
            ] as { id: "all" | HostedBy; label: string }[]).map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setFilterHost(t.id);
                  setFilterChapterId("");
                  setFilterInstituteId("");
                }}
                className={`px-3 sm:px-4 py-2 sm:py-1.5 text-xs font-black uppercase tracking-wider rounded-xl transition-all whitespace-nowrap ${
                  filterHost === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-400 hover:text-gray-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Chapter sub-filter */}
        {(filterHost === "all" || filterHost === "chapter") && (
          <select
            value={filterChapterId}
            onChange={(e) => { setFilterChapterId(e.target.value); setFilterHost("chapter"); if (!e.target.value) setFilterHost("all"); }}
            className="w-full sm:w-auto min-w-0 border rounded-xl px-3 py-2.5 sm:py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-100 bg-white"
          >
            <option value="">All Chapters</option>
            {(chapters as any[]).map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        {/* Institute sub-filter */}
        {(filterHost === "all" || filterHost === "institute") && (
          <select
            value={filterInstituteId}
            onChange={(e) => { setFilterInstituteId(e.target.value); setFilterHost("institute"); if (!e.target.value) setFilterHost("all"); }}
            className="w-full sm:w-auto min-w-0 border rounded-xl px-3 py-2.5 sm:py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-100 bg-white"
          >
            <option value="">All Institutions</option>
            {(institutes as any[]).map((i: any) => (
              <option key={i.id} value={String(i.id)}>{i.name}{i.acronym ? ` (${i.acronym})` : ""}</option>
            ))}
          </select>
        )}

        <div className="sm:ml-auto text-xs text-gray-400 font-semibold sm:shrink-0">
          {filtered.length} of {(events as any[]).length} event{(events as any[]).length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Global msg */}
      {msg && (
        <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border text-sm font-bold ${msg.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"}`}>
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center">
          <CalendarCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 font-semibold text-sm sm:text-base">
            {(events as any[]).length === 0 ? "No events yet. Create your first event." : "No events match the selected filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((event: any) => (
            <div key={event.id} className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center shrink-0">
                    <CalendarCheck className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-black text-gray-900 break-words">{event.title}</div>
                    {event.theme && <div className="text-xs text-gray-400 italic mt-0.5">"{event.theme}"</div>}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs text-gray-500 font-medium">
                        {event.start_date ? fmt(event.start_date) : "—"}
                        {event.end_date && event.end_date !== event.start_date ? ` → ${fmt(event.end_date)}` : ""}
                      </span>
                      {event.location && <span className="text-xs text-gray-400 font-medium">· {event.location}</span>}
                      {event.hosted_by === "chapter" && event.host_chapter && (
                        <span className="flex items-center gap-1 text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-lg">
                          <Building2 className="w-3 h-3" /> {event.host_chapter.name}
                        </span>
                      )}
                      {event.hosted_by === "institute" && event.host_institute && (
                        <span className="flex items-center gap-1 text-xs font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-lg">
                          <Building2 className="w-3 h-3" /> {event.host_institute.acronym ?? event.host_institute.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* On a phone the controls sit on their own row under the
                    event, spaced for thumbs rather than crowded beside it. */}
                <div className="flex items-center gap-2 flex-wrap flex-shrink-0 border-t md:border-0 border-gray-50 pt-3 md:pt-0">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl border ${STATUS_COLORS[event.status] ?? "bg-gray-50 text-gray-500 border-gray-100"}`}>
                    {event.status}
                  </span>

                  {/* Each control is gated on the permission its endpoint
                      demands — publish/cancel on event.lifecycle, edit on
                      event.update, delete on event.delete. The API refuses
                      without them, so showing the button only offers an admin
                      an action that ends in a 403. */}
                  <AccessGate perm={PERMS.event.lifecycle}>
                    {event.status === "draft" && (
                      <button
                        onClick={() => handlePublish(event)}
                        title="Publish"
                        className="p-2.5 md:p-2 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                      >
                        <Globe className="w-4 h-4" />
                      </button>
                    )}
                    {event.status === "published" && (
                      <button
                        onClick={() => handleCancel(event)}
                        title="Cancel"
                        className="p-2.5 md:p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </AccessGate>

                  <AccessGate perm={PERMS.event.update}>
                    <button
                      onClick={() => openEdit(event)}
                      title="Edit"
                      className="p-2.5 md:p-2 rounded-xl bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </AccessGate>
                  <AccessGate perm={PERMS.event.delete}>
                    <button
                      onClick={() => { setDeleteTarget(event); setDeleteError(""); }}
                      title="Delete"
                      className="p-2.5 md:p-2 rounded-xl bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </AccessGate>
                  <Link
                    to={`/admin/events/${event.id}`}
                    title="Open"
                    className="ml-auto md:ml-0 inline-flex items-center gap-1.5 p-2.5 md:p-2 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                  >
                    <span className="md:hidden text-[10px] font-black uppercase tracking-widest pl-1">Open</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit slide-in */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeForm} />
          {/* Full-height sheet on a phone, centred dialog from `sm` up. */}
          <div className="relative w-full max-w-2xl bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
              <h2 className="font-black text-gray-900 text-base sm:text-lg uppercase tracking-tight">
                {editTarget ? "Edit Event" : "New Event"}
              </h2>
              <button onClick={closeForm} className="p-2 rounded-xl hover:bg-gray-50 transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-5 overflow-y-auto flex-1">
              {/* Basic info */}
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Title *</label>
                <input className={inputCls} value={form.title} onChange={set("title")} placeholder="Event title" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Theme</label>
                <input className={inputCls} value={form.theme} onChange={set("theme")} placeholder="Conference theme" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tagline</label>
                <input className={inputCls} value={form.tagline} onChange={set("tagline")} placeholder="Short tagline" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Details</label>
                <textarea className={textareaCls} rows={4} value={form.details} onChange={set("details")} placeholder="Full event description..." />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Location</label>
                <input className={inputCls} value={form.location} onChange={set("location")} placeholder="Venue" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Start Date *</label>
                  <input type="date" className={inputCls} value={form.start_date} onChange={set("start_date")} />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">End Date *</label>
                  <input type="date" className={inputCls} value={form.end_date} onChange={set("end_date")} />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Year <span className="normal-case font-medium text-gray-300">(optional)</span></label>
                <input type="number" min={1990} max={2100} className={inputCls} value={form.year} onChange={set("year")} placeholder="e.g. 2026" />
              </div>

              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Capacity</label>
                <input type="number" className={inputCls} value={form.capacity} onChange={set("capacity")} placeholder="Max attendees (optional)" />
              </div>

              {/* Hosted By */}
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Hosted By</label>
                <select
                  className={inputCls}
                  value={form.hosted_by}
                  onChange={(e) => setForm((p) => ({
                    ...p,
                    hosted_by: e.target.value as HostedBy,
                    host_chapter_id: "",
                    host_accredited_institute_id: "",
                  }))}
                >
                  <option value="niqs">NIQS (National Institute)</option>
                  <option value="chapter">State Chapter</option>
                  <option value="institute">Accredited External Institute</option>
                </select>
              </div>

              {form.hosted_by === "chapter" && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Chapter *</label>
                  <select
                    className={inputCls}
                    value={form.host_chapter_id}
                    onChange={(e) => setForm((p) => ({ ...p, host_chapter_id: e.target.value }))}
                  >
                    <option value="">— Select a chapter —</option>
                    {(chapters as any[]).map((c: any) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.hosted_by === "institute" && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Accredited Institute *</label>
                  <select
                    className={inputCls}
                    value={form.host_accredited_institute_id}
                    onChange={(e) => setForm((p) => ({ ...p, host_accredited_institute_id: e.target.value }))}
                  >
                    <option value="">— Select an institute —</option>
                    {(institutes as any[]).filter((i: any) => i.is_active).map((i: any) => (
                      <option key={i.id} value={i.id}>{i.name}{i.acronym ? ` (${i.acronym})` : ""}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Toggles */}
              <div className="space-y-3 pt-2">
                {[
                  { key: "is_paid" as const,        label: "Paid Event"   },
                  { key: "is_cpd" as const,         label: "CPD Event"    },
                  { key: "is_members_only" as const, label: "Members Only" },
                  { key: "has_refreshment" as const, label: "Refreshments" },
                  { key: "has_materials" as const,   label: "Materials"    },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-700">{label}</span>
                    <button
                      onClick={() => toggle(key)}
                      className={`w-10 h-6 rounded-full transition-colors ${form[key] ? "bg-indigo-500" : "bg-gray-200"}`}
                    >
                      <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form[key] ? "translate-x-4" : "translate-x-0"}`} />
                    </button>
                  </div>
                ))}
              </div>

              {form.is_paid && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Default Price (₦)</label>
                  <input type="number" className={inputCls} value={form.price} onChange={set("price")} placeholder="0.00" />
                  <p className="text-[10px] text-gray-400 mt-1">Charged when no category price below applies.</p>
                </div>
              )}

              {form.is_paid && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fee Timeline</label>
                  <p className="text-[10px] text-gray-400 mt-0.5 mb-2">
                    Optional. Set these and each category can carry an early bird and a late fee
                    as well as its normal one. Leave them blank and every category charges one fee.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-[10px] font-bold text-gray-500">Early bird ends</span>
                      <input type="date" className={inputCls} value={form.early_bird_ends_at} onChange={set("early_bird_ends_at")} />
                      <p className="text-[9px] text-gray-400 mt-1">Last day on the early bird fee.</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-gray-500">Late fee starts</span>
                      <input type="date" className={inputCls} value={form.late_starts_at} onChange={set("late_starts_at")} />
                      <p className="text-[9px] text-gray-400 mt-1">First day on the late fee.</p>
                    </div>
                  </div>
                </div>
              )}

              {form.is_paid && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Prices by Category (₦)</label>
                  <p className="text-[10px] text-gray-400 mt-0.5 mb-2">
                    Set a fee per category. Members are charged their grade's price automatically;
                    guests choose a tier at registration. A category with no Normal fee is not offered.
                    {hasVirtualSeats
                      ? " A Virtual fee makes that category bookable online as well as in the hall."
                      : ""}
                  </p>

                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full min-w-[420px] text-xs">
                      <thead>
                        <tr className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                          <th className="text-left font-black pb-1">Category</th>
                          {showEarly && <th className="font-black pb-1 w-24">Early bird</th>}
                          <th className="font-black pb-1 w-24">Normal</th>
                          {showLate && <th className="font-black pb-1 w-24">Late</th>}
                          <th className="font-black pb-1 w-24">Virtual</th>
                          <th className="font-black pb-1 w-16">ID card</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priceRows.map((t) => {
                          const fees = catPrices[t.key] ?? emptyFees;
                          type FeeField = "early" | "normal" | "late" | "virtual";
                          const cell = (field: FeeField, placeholder: string) => (
                            <td className="py-1 align-middle">
                              <input
                                type="number"
                                min={0}
                                className="w-full border rounded-lg px-2 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                                value={fees[field] ?? ""}
                                onChange={(e) =>
                                  setCatPrices((p) => ({
                                    ...p,
                                    [t.key]: { ...emptyFees, ...(p[t.key] ?? {}), [field]: e.target.value },
                                  }))
                                }
                                placeholder={placeholder}
                              />
                            </td>
                          );

                          return (
                            <tr key={t.key} className="align-middle">
                              <td className="pr-3 py-1 text-xs font-semibold text-gray-600">
                                {t.label}
                                {t.user_category_id == null && <span className="ml-1 text-[9px] text-gray-300 uppercase">(public)</span>}
                              </td>
                              {showEarly && cell("early", "—")}
                              {cell("normal", "—")}
                              {showLate && cell("late", "—")}
                              {cell("virtual", "—")}
                              <td className="py-1 text-center align-middle">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 accent-indigo-600"
                                  checked={!!fees.idCard}
                                  onChange={(e) =>
                                    setCatPrices((p) => ({
                                      ...p,
                                      [t.key]: { ...emptyFees, ...(p[t.key] ?? {}), idCard: e.target.checked },
                                    }))
                                  }
                                  aria-label={`Require an ID card for ${t.label}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-[10px] text-gray-400 mt-2">
                    <strong>Normal</strong> is the fee that makes a category offered — leave it blank
                    and that category is not on sale. <strong>Early bird</strong> and{" "}
                    <strong>Late</strong> are optional: fill them in only where the category is
                    cheaper before the cut-off or dearer after it, and a blank one charges the Normal
                    fee for that window. Tick <strong>ID card</strong> where the rate is only open to
                    a particular kind of delegate: registration then refuses that category without an
                    uploaded card.
                  </p>
                </div>
              )}

              {form.is_cpd && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">CPD Points</label>
                  <input type="number" className={inputCls} value={form.cpd_points} onChange={set("cpd_points")} placeholder="e.g. 5" />
                </div>
              )}

              <CertificateTemplateField form={form} setForm={setForm} />

              {/* Images */}
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Background Image</label>
                  <label className="flex items-center gap-2 border-b-2 border-gray-100 py-2 cursor-pointer hover:border-indigo-500 transition-colors">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">{bgFile?.name ?? "Choose file…"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setBgFile(e.target.files?.[0] ?? null)} />
                  </label>
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Foreground Image</label>
                  <label className="flex items-center gap-2 border-b-2 border-gray-100 py-2 cursor-pointer hover:border-indigo-500 transition-colors">
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">{fgFile?.name ?? "Choose file…"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setFgFile(e.target.files?.[0] ?? null)} />
                  </label>
                </div>
              </div>

            </div>

            <div className="p-4 sm:p-6 border-t border-gray-100 shrink-0 bg-white space-y-3">
              {formError && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium px-4 py-3 rounded-xl">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span className="whitespace-pre-line">{formError}</span>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={closeForm}
                  disabled={creating || updating}
                  className="px-5 py-3 rounded-2xl border text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={creating || updating}
                  className="flex-1 bg-gray-900 text-white font-black text-sm py-3.5 rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {(creating || updating) ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {editTarget ? "Save Changes" : "Create Event"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-8 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="font-black text-gray-900 text-lg mb-2">Delete Event?</h3>
            <p className="text-sm text-gray-500 font-medium mb-5">
              "<strong>{deleteTarget.title}</strong>" will be permanently deleted.
            </p>
            {deleteError && (
              <div className="mb-4 text-rose-600 text-sm font-medium bg-rose-50 border border-rose-100 px-4 py-3 rounded-2xl">
                {deleteError}
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 border border-gray-100 font-black text-sm py-3 rounded-2xl text-gray-500 hover:bg-gray-50 transition-colors">Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-rose-500 text-white font-black text-sm py-3 rounded-2xl hover:bg-rose-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-900 rounded-3xl sm:rounded-[2.5rem] p-5 sm:p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <CalendarCheck className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">Event Lifecycle</div>
            <p className="font-bold text-xs sm:text-sm text-white/80">
              Draft → Publish to open registration → Complete to allocate CPD points.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Certificate format ───────────────────────────────────────────────────────
/**
 * Which attendance certificate this event prints, with the chosen design
 * shown as it will come out of the printer.
 *
 * Previewed rather than listed by name because the choice is a visual one and
 * the names ("Ribbon", "Chevron") mean nothing until you have seen the sheet.
 * The preview is filled with whatever of the event is already typed in, so the
 * admin is looking at THEIR event on the design and not a generic sample — a
 * title that turns out to be two lines long is worth finding out here.
 */
function CertificateTemplateField({
  form, setForm,
}: {
  form: EventForm;
  setForm: React.Dispatch<React.SetStateAction<EventForm>>;
}) {
  const { data: templates = [], isLoading } = useAdminGetCertificateTemplatesQuery();
  const chosen = (templates as any[]).find((t) => t.key === form.certificate_template);
  const sigs = form.certificate_signatures;

  /** The blocks a design is drawn with, as form rows. */
  const suggested = (key: string): CertSignature[] =>
    (((templates as any[]).find((x) => x.key === key)?.suggested_signatures ?? []) as any[])
      .map((x) => ({ ...emptySignature, name: x?.name ?? "", title: x?.title ?? "" }));

  /** Whether these rows are still a design's untouched prefill. */
  const isUntouched = (rows: CertSignature[], key: string) => {
    const sug = suggested(key);
    return rows.length === sug.length
      && rows.every((r, i) =>
        r.name.trim() === sug[i].name
        && r.title.trim() === sug[i].title
        && !r.signature_path
        && !r.file);
  };

  const differsFromDesign = !!chosen && !isUntouched(sigs, chosen.key);

  /**
   * Picking a design brings the blocks it was drawn with — unless the admin
   * has edited the ones already there, which are then left alone.
   *
   * The middle case is what matters: blocks that are still the PREVIOUS
   * design's untouched prefill are nobody's work, so switching from a sheet
   * drawn with two signatures to one drawn with three has to bring the third
   * along. Holding on to them because the list was merely non-empty is how a
   * biennial certificate ends up printing two.
   */
  const pickTemplate = (key: string) => {
    setForm((f) => {
      const rows = f.certificate_signatures;
      const replaceable =
        rows.length === 0
        || (!!f.certificate_template && isUntouched(rows, f.certificate_template));

      if (!key || !replaceable) {
        return { ...f, certificate_template: key };
      }
      return { ...f, certificate_template: key, certificate_signatures: suggested(key) };
    });
  };

  /** Puts the chosen design's own blocks back, discarding what is there. */
  const resetSignatures = () =>
    setForm((f) => ({ ...f, certificate_signatures: suggested(f.certificate_template) }));

  const setSig = (i: number, field: keyof CertSignature) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      certificate_signatures: f.certificate_signatures.map((x, n) => (n === i ? { ...x, [field]: v } : x)),
    }));
  };

  const addSig = () =>
    setForm((f) => ({ ...f, certificate_signatures: [...f.certificate_signatures, { ...emptySignature }] }));

  /** A newly chosen scan. Previewed from a blob so it shows before any save. */
  const setSigFile = (i: number, file: File | null) =>
    setForm((f) => ({
      ...f,
      certificate_signatures: f.certificate_signatures.map((x, n) =>
        n === i
          ? { ...x, file, signature_url: file ? URL.createObjectURL(file) : x.signature_url }
          : x),
    }));

  /** Clears all three, which is what tells the server to drop the scan. */
  const clearSigFile = (i: number) =>
    setForm((f) => ({
      ...f,
      certificate_signatures: f.certificate_signatures.map((x, n) =>
        n === i ? { ...x, file: null, signature_path: "", signature_url: "" } : x),
    }));

  const removeSig = (i: number) =>
    setForm((f) => ({ ...f, certificate_signatures: f.certificate_signatures.filter((_, n) => n !== i) }));

  return (
    <div>
      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
        <Award className="w-3 h-3" /> Certificate Format
      </label>

      <select
        className={inputCls}
        value={form.certificate_template}
        onChange={(e) => pickTemplate(e.target.value)}
        disabled={isLoading}
      >
        <option value="">No certificate</option>
        {(templates as any[]).map((t) => (
          <option key={t.key} value={t.key}>{t.name}</option>
        ))}
      </select>

      {!chosen && (
        <p className="mt-2 text-[11px] text-gray-400 font-medium">
          Optional. Pick a format and delegates can print an attendance certificate for this event.
        </p>
      )}

      {chosen && (
        <div className="mt-3 space-y-4">
          <p className="text-[11px] text-gray-400 font-medium leading-relaxed">{chosen.description}</p>

          {/* ── Signatures ───────────────────────────────────────────────
              On the event, not the design: a biennial conference is signed by
              the BGM committee chairman and the host chapter chairman, a
              workshop is not, and whoever holds each office changes while the
              artwork does not. */}
          <div className="space-y-2.5">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Signatures
            </div>

            {sigs.length === 0 && (
              <p className="text-[11px] text-gray-400 font-medium">
                No signature blocks — the certificate prints with none.
              </p>
            )}

            {sigs.map((sig, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-[10px] font-black text-gray-300 pt-3 w-3 shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                    <input
                      className={inputCls}
                      value={sig.name}
                      onChange={setSig(i, "name")}
                      placeholder="Name (optional)"
                    />
                    <input
                      className={inputCls}
                      value={sig.title}
                      onChange={setSig(i, "title")}
                      placeholder="Office, e.g. NIQS President"
                    />
                  </div>

                  {/* The scan. Shown at the size it prints, on the rule. */}
                  <div className="flex items-center gap-2 pt-1">
                    {sig.signature_url ? (
                      <>
                        <img
                          src={sig.signature_url}
                          alt=""
                          className="h-8 max-w-[7rem] object-contain mix-blend-multiply"
                        />
                        <button
                          type="button"
                          onClick={() => clearSigFile(i)}
                          className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-rose-500"
                        >
                          Remove
                        </button>
                      </>
                    ) : (
                      <label className="inline-flex items-center gap-1.5 cursor-pointer text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-indigo-600">
                        <Upload className="w-3 h-3" /> Signature image
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={(e) => setSigFile(i, e.target.files?.[0] ?? null)}
                        />
                      </label>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeSig(i)}
                  title="Remove this signature"
                  className="pt-2.5 text-gray-300 hover:text-rose-500 shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            <div className="flex items-center gap-4 flex-wrap">
              {sigs.length < MAX_SIGNATURES && (
                <button
                  type="button"
                  onClick={addSig}
                  className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-700"
                >
                  <Plus className="w-3 h-3" /> Add signature
                </button>
              )}

              {/* Only once the blocks have drifted from the design's own —
                  otherwise it is a button that visibly does nothing. */}
              {differsFromDesign && (
                <button
                  type="button"
                  onClick={resetSignatures}
                  title={`Replace these with the ${chosen.name} blocks`}
                  className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-indigo-600"
                >
                  Use this design&rsquo;s signatures
                </button>
              )}
            </div>

            <p className="text-[11px] text-gray-400 font-medium leading-relaxed">
              The office is what prints; the name is optional and sits above it. A signature
              image, if uploaded, prints on the rule — a PNG with a transparent or white
              background works best.
            </p>
          </div>

          {/* The admin's own event on the chosen design, so a title that turns
              out to run to two lines is found here and not after printing. */}
          <div className="rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
            <CertificatePreview
              data={sampleCertificate(
                chosen.key,
                sigs
                  .filter((x) => x.title.trim() !== "")
                  .map((x) => ({ name: x.name, title: x.title, signature_url: x.signature_url || null })),
                {
                  title: form.title,
                  theme: form.theme,
                  year: form.year,
                  location: form.location,
                  start_date: form.start_date,
                  end_date: form.end_date,
                  cpd_points: form.is_cpd ? form.cpd_points : "",
                },
              )}
            />
          </div>

          <p className="text-[11px] text-gray-400 font-medium">
            Members print this once CPD has been issued for the event. The name shown is a sample.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Gated on the events-section read capability.
 */
export default function AdminEventsPage() {
  return (
    <PageGate perm={PERMS.event.access}>
      <AdminEventsPageInner />
    </PageGate>
  );
}
